// istanbul ignore file
import Highcharts from 'highcharts'
import { type Options, type SeriesAreasplineOptions } from 'highcharts'
import boost from 'highcharts/modules/boost'
import moment from 'moment-timezone'
import { type Option } from '../../../components/GenXDropdown'
import { xAxisLabelFormatter, tooltipPositioner, customTooltipPointFormatter } from '../../device-dashboard/components/ChartSetup'

// Initialize the boost module
boost(Highcharts)

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

export function getFlameSignalDataOptions (
  ModBusParameter1: any = [[null, null, null]], unit1 = '', registerName1 = '',
  ModBusParameter2: any = [[null, null, null]], unit2 = '', registerName2 = '',
  ModBusParameter3: any = [[null, null, null]], unit3 = '', registerName3 = '',
  ModBusParameter4: any = [[null, null, null]], unit4 = '', registerName4 = ''
): Options {
  const colorMap = ['#9AB7FF', '#FFF55D', '#f2752c', '#19CB35']
  const seriesData: SeriesAreasplineOptions[] = [
    {
      type: 'areaspline',
      name: `${registerName1}`,
      data: ModBusParameter1,
      lineWidth: 4,
      lineColor: '#9AB7FF',
      marker: {
        enabled: true,
        symbol: 'circle',
        radius: 4,
        fillColor: '#9AB7FF',
        lineColor: '#ffffff',
        lineWidth: 1,
        states: {
          hover: { enabled: true }
        },
        fillOpacity: 1
      }
    },
    {
      type: 'areaspline',
      name: `${registerName2}`,
      data: ModBusParameter2,
      lineWidth: 4,
      lineColor: '#FFF55D',
      marker: {
        enabled: true,
        symbol: 'circle',
        radius: 4,
        fillColor: '#FFF55D',
        lineColor: '#FFF55D',
        lineWidth: 1,
        states: {
          hover: { enabled: true }
        },
        fillOpacity: 1
      }
    },
    {
      type: 'areaspline',
      name: `${registerName3}`,
      data: ModBusParameter3,
      lineWidth: 4,
      lineColor: '#f2752c',
      marker: {
        enabled: true,
        symbol: 'circle',
        radius: 4,
        fillColor: '#f2752c',
        lineColor: '#f2752c',
        lineWidth: 1,
        states: {
          hover: { enabled: true }
        },
        fillOpacity: 1
      }
    },
    {
      type: 'areaspline',
      name: `${registerName4}`,
      data: ModBusParameter4,
      lineWidth: 4,
      lineColor: '#19CB35',
      marker: {
        enabled: true,
        symbol: 'circle',
        radius: 4,
        fillColor: '#19CB35',
        lineColor: '#19CB35',
        lineWidth: 1,
        states: {
          hover: { enabled: true }
        },
        fillOpacity: 1
      }
    }
  ]

  return {
    chart: {
      height: 280,
      type: 'areaspline',
      zooming: {
        type: 'xy'
      },
      style: {
        fontSize: '16px',
        fontFamily: 'Honeywell Sans Web'
      }
    },
    credits: {
      enabled: false
    },
    exporting: {
      enabled: false,
      buttons: {
        contextButton: {
          menuItems: [
            'downloadJPEG'
          ]
        }
      }
    },
    title: {
      text: ''
    },
    series: seriesData,
    legend: {
      enabled: true,
      itemStyle: {
        fontWeight: '400',
        fontSize: '16px'
      },
      backgroundColor: 'transparent',
      useHTML: true,
      labelFormatter: function () {
        const name = this.name
        const color = colorMap[this.index]
        return `<div style="position: relative; text-align: center;"><span class='icon-class' style="background-color: ${this.visible ? color : '#9AB7FF'}; display: ${(this.name.length > 0) ? 'inline-table' : 'none'}"></span><span class='icon-class-title' style="color: '#f0f0f0; display: inline-block; text-align: center;">${name}</span></div>`
      }
    },
    xAxis: {
      title: {
        text: ''
      },
      type: 'datetime',
      labels: {
        formatter: function () {
          return moment(this.value).utc().format('M/D/YYYY') + '<br>' + moment(this.value).utc().format('hh:mm:ss A')
        }
      },
      minRange: 3600 * 1000 // Minimum range of 1 hour in milliseconds
    },
    yAxis: {
      title: {
        text: ''
      },
      startOnTick: true,
      endOnTick: true
    },
    plotOptions: {
      series: {
        marker: {
          enabled: true,
          radius: 2
        },
        dataGrouping: {
          enabled: true
          // Adjust based on the data size
        }
      },
      areaspline: {
        fillOpacity: 0,
        states: {
          hover: {
            lineWidth: 2
          }
        },
        marker: {
          enabled: true // Show markers on hover
        }
      }
    },
    lang: {
      resetZoom: 'Reset Zoom'
    },
    tooltip: {
      backgroundColor: '#F0F0F0',
      style: {
        color: 'black', // Set the text color
        fontFamily: '"Honeywell Sans Web", Arial, Helvetica, sans-serif',
        fontSize: '15px',
        fontWeight: '500'
      },
      formatter: function () {
        let unit = '' // Default unit
        // Determine the series and assign the corresponding unit
        switch (this.series.name) {
          case registerName1:
            unit = unit1 // Assuming unit1 is accessible in this scope
            break
          case registerName2:
            unit = unit2 // Assuming unit2 is accessible in this scope
            break
          case registerName3:
            unit = unit3 // Assuming unit3 is accessible in this scope
            break
          case registerName4:
            unit = unit4 // Assuming unit4 is accessible in this scope
            break
          default:
            unit = '' // Default or unknown series
        }
        const newdate = (this.x !== undefined && this.x !== null) ? moment(this.x).tz(userTimezone).format('YYYY-MM-DD hh:mm:ss A') : 'Invalid Date'
        let indexValue = 'Not Available'
        if (this.y !== undefined && this.y !== null) {
          indexValue = this.y.toString()
        } else {
          indexValue = 'Not Available'
        }
        return (
          `<span style="background-color:#F0F0F0; color:black; padding:5px; border-radius:5px;">
          <span style="color:black">•&nbsp;&nbsp;</span> ${newdate}<br/>•&nbsp;&nbsp;
          ${String(this.series.name)}<br/>•&nbsp;&nbsp;Unit : ${unit}
          <br/>•&nbsp;&nbsp;Value : ${indexValue}
          </span>`
        )
      }
    },
    boost: {
      useGPUTranslations: true,
      usePreallocated: true
    }
  }
}

export function generateAssetPerformanceChartOptions (data: any, lowerLimit: number, higherLimit: number, color: any, name: string): Highcharts.Options {
  return {
    chart: {
      type: 'line',
      height: 110,
      zooming: {
        type: 'xy'
      }
    },
    title: { text: '' },
    exporting: {
      enabled: false,
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen' // Add the option to view the graph in fullscreen
          ]
        }
      }
    },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      title: { text: '' },
      lineColor: '#707070', // Adds a visible x-axis line
      lineWidth: 0, // Thickness of the x-axis line
      tickLength: 0, // Removes tick marks
      labels: {
        enabled: false // Disables labels
      },
      gridLineWidth: 0 // Removes grid lines on the x-axis
    },
    yAxis: {
      title: { text: '' },
      min: -10,
      lineWidth: 0, // Thickness of the y-axis line
      tickLength: 0, // Removes tick marks
      labels: {
        enabled: false // Disables labels
      },
      gridLineWidth: 0, // Removes grid lines on the y-axis
      gridLineColor: '#707070',
      plotBands: [{
        color: '#707070',
        from: lowerLimit,
        to: higherLimit
      }]
    },
    series: [{
      type: 'line',
      name,
      data: data.map((item: { value: number, indexValue: number, readingTime: string }) => [new Date(item.readingTime).getTime(), item.indexValue !== undefined ? item.indexValue : item.value]),
      color,
      states: {
        hover: {
          enabled: false
        }
      },
      zones: [
        {
          value: lowerLimit,
          color: (lowerLimit === 0 && higherLimit === 0) ? 'gray' : 'red'
        },
        {
          value: higherLimit,
          color: 'rgba(240, 240, 240, 0.4)'
        },
        {
          color: (lowerLimit === 0 && higherLimit === 0) ? 'gray' : 'red'
        }
      ]
    }],
    plotOptions: {
      series: {
        turboThreshold: 1000000,
        dataGrouping: {
          enabled: false
        }
      }
    },
    boost: {
      useGPUTranslations: true,
      usePreallocated: true
    },
    tooltip: {
      enabled: true,
      backgroundColor: '#F0F0F0',
      style: {
        color: 'black',
        fontFamily: '"Honeywell Sans Web", Arial, Helvetica, sans-serif',
        fontSize: '12px',
        padding: '5px',
        fontWeight: '500'
      },
      formatter: function () {
        const date = (this.x !== undefined && this.x !== null) ? moment(this.x).tz(userTimezone).format('YYYY-MM-DD hh:mm:ss A') : 'Invalid Date'
        const limit = (lowerLimit === 0 && higherLimit === 0) ? 'Alarm not enabled' : `L: ${lowerLimit}, H: ${higherLimit}`
        return `Date: ${date} Value : ${this.y ?? 'Not Available'}, ${limit}`
      }
    }
  }
}

export interface IConfigObjectItem {
  serialNumber: string
  checked: boolean
  color: string
  selectedSignalData: null | Option
  isDeviceKPI: boolean
  isAssetKPI: boolean
  selectedDeviceAssetID: string
  index: number
}
export type IConfigObject = Record<number, IConfigObjectItem>

interface CustomPoint {
  x: number
  y: number
  units: string
  registerName: string
  readingTime: string
}

export const getFlameSignalDataOptionsNew = (data: any[], configObject: IConfigObject): Highcharts.Options => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const getSeriesObject = (readings: any, entry: any): any => {
    const readingTime = moment.utc(entry.readingTime).tz(userTimezone)
    return {
      x: Date.UTC(readingTime.year(), readingTime.month(), readingTime.date(), readingTime.hours(), readingTime.minutes(), readingTime.seconds()),
      y: entry.indexValue !== undefined ? entry.indexValue : entry.value,
      units: readings[0].units,
      registerName: readings[0].registerName,
      readingTime: entry.readingTime
    }
  }

  // Rearrange data and config for yAxis mapping
  const checkedConfigs = Object.values(configObject).filter(config => config.checked)
  const rearrangedData = checkedConfigs.map(config => {
    const item = data.find(d =>
      (d.deviceId === config.selectedDeviceAssetID || d.assetId === config.selectedDeviceAssetID) &&
      (Array.isArray(d.registerReadings) ? d.registerReadings[0]?.cimCodeId === config.selectedSignalData?.value : Array.isArray(d.kpiReadings) ? d.kpiReadings[0]?.kpiDefinitionId === config.selectedSignalData?.value : false)
    )
    return item != null ? { ...item, color: config.color, config } : null
  }).filter(item => item !== null)

  // Build series with yAxis index
  const series: Highcharts.SeriesLineOptions[] = rearrangedData.map((item, idx) => {
    const readings = Array.isArray(item.registerReadings) ? item.registerReadings : (Array.isArray(item.kpiReadings) ? item.kpiReadings : [])
    const name = Array.isArray(item.registerReadings) ? 'registerName' : 'kpiName'
    let seriesData: CustomPoint[] = []
    if (name === 'registerName') {
      seriesData = readings[0].registerReadings.map((entry: any) => getSeriesObject(readings, entry))
    } else {
      seriesData = readings[0].kpiReadings.map((entry: any) => getSeriesObject(readings, entry))
    }
    return {
      type: 'line',
      name: readings[0]?.[name] === undefined ? '' : readings[0]?.[name],
      data: seriesData,
      color: item.color,
      dataGrouping: {
        enabled: true,
        approximation: 'average',
        groupPixelWidth: 10,
        units: [['hour', [1]], ['day', [1]], ['week', [1]], ['month', [1]] as [string, number[] | null]]
      },
      fillOpacity: 0,
      lineWidth: 1,
      marker: {
        enabled: true,
        radius: 0,
        symbol: 'circle'
      },
      turboThreshold: 500000,
      compare: 'percent',
      showInNavigator: true,
      yAxis: idx // Assign each series to its own yAxis
    }
  })

  // Create a yAxis for each series for autoscaling
  const yAxes: Highcharts.YAxisOptions[] = rearrangedData.length === 0
    ? [{ visible: false }]
    : rearrangedData.map((item, idx) => {
      // Determine if there is data for this axis
      let hasData = false
      if (Array.isArray(item.registerReadings) && item.registerReadings.length > 0) {
        hasData = Array.isArray(item.registerReadings[0]?.registerReadings) && item.registerReadings[0].registerReadings.length > 0
      } else if (Array.isArray(item.kpiReadings) && item.kpiReadings.length > 0) {
        hasData = Array.isArray(item.kpiReadings[0]?.kpiReadings) && item.kpiReadings[0].kpiReadings.length > 0
      }
      return {
        title: {
          text: Array.isArray(item.registerReadings)
            ? item.registerReadings[0]?.registerName
            : Array.isArray(item.kpiReadings)
              ? item.kpiReadings[0]?.kpiName
              : '',
          style: {
            visibility: 'hidden'
          }
        },
        labels: {
          style: {
            color: item.color,
            fontSize: '14px'
          }
        },
        opposite: idx % 2 === 1,
        min: undefined,
        max: undefined,
        showLastLabel: true,
        gridLineWidth: 0,
        lineWidth: hasData ? 2 : 0,
        lineColor: item.color,
        visible: hasData
      }
    })

  if (series.length === 0) {
    return {
      chart: {
        height: 380,
        zooming: {
          type: 'xy'
        },
        type: 'line',
        panning: {
          enabled: true,
          type: 'x'
        },
        panKey: 'shift',
        style: {
          fontSize: '16px',
          fontFamily: 'Honeywell Sans Web'
        },
        events: {
          load: function () {
            this.showLoading('Loading data...')
          }
        }
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: ['viewFullscreen']
          }
        }
      },
      title: {
        text: '',
        align: 'center',
        verticalAlign: 'middle',
        style: {
          fontSize: '16px',
          fontFamily: 'Honeywell Sans Web'
        }
      },
      xAxis: {
        crosshair: true,
        title: {
          text: ''
        },
        type: 'datetime',
        labels: {
          formatter: xAxisLabelFormatter,
          useHTML: true,
          step: 2,
          allowOverlap: false
        },
        minRange: 60 * 1000
      },
      yAxis: yAxes,
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemMarginTop: 10,
        itemMarginBottom: 10,
        itemStyle: {
          fontWeight: '400',
          fontSize: '16px',
          fontFamily: '"Honeywell Sans Web", Arial, Helvetica, sans-serif'
        },
        backgroundColor: 'transparent',
        useHTML: true,
        labelFormatter: function () {
          const name = this.name
          const color = this.color
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          return `<div style="position: relative;"><span class='icon-class' style="background-color: ${this.visible ? color : color}; display: ${(this.name.length > 0) ? 'inline-table' : 'none'}"></span><span class='icon-class-title' style="color: '#f0f0f0'">${name}</span></div>`
        }
      },
      tooltip: {
        shared: true,
        split: true,
        valueDecimals: 0,
        xDateFormat: '%b %e, %Y %I:%M:%S %p',
        pointFormatter: customTooltipPointFormatter,
        positioner: tooltipPositioner
      },
      plotOptions: {
        series: {
          animation: false
        }
      },
      boost: {
        useGPUTranslations: true,
        usePreallocated: true
      },
      series,
      lang: {
        noData: 'No Data Available'
      },
      noData: {
        style: {
          fontWeight: 'bold',
          fontSize: '15px',
          color: '#f2f2f2'
        }
      },
      loading: {
        labelStyle: {
          color: 'white',
          fontWeight: 'bold',
          fontSize: '15px'
        },
        style: {
          backgroundColor: 'transparent'
        }
      }
    }
  }

  return {
    chart: {
      height: 380,
      zooming: {
        type: 'xy'
      },
      type: 'line',
      panning: {
        enabled: true,
        type: 'x'
      },
      panKey: 'shift',
      style: {
        fontSize: '16px',
        fontFamily: 'Honeywell Sans Web'
      },
      events: {
        load: function (this: Highcharts.Chart) {
          this.showLoading('Loading data...')
          setTimeout(() => {
            this.hideLoading()
          }, 2000)
        },
        redraw: function (this: Highcharts.Chart) {
          this.hideLoading()
        },
        render: function (this: Highcharts.Chart) {
          // Remove previous legendItemClick handlers to avoid duplicates
          this.series.forEach((series, idx: number) => {
            Highcharts.removeEvent(series, 'legendItemClick')
            Highcharts.addEvent(series, 'legendItemClick', function (event: any) {
              const chart = this.chart
              const yAxis = chart.yAxis[idx]
              if (yAxis !== undefined && yAxis !== null) {
                // Toggle visibility based on what it will be after click
                const willBeVisible = !this.visible
                yAxis.update({ visible: willBeVisible }, false)
                chart.redraw()
              }
            })
          })

          // Keep your resetZoomButton alignment logic if needed
          const chart = this as Highcharts.Chart & { resetZoomButton?: any }
          const button = chart.resetZoomButton
          if (button?.element !== undefined) {
            button.align({
              align: 'left',
              x: 120,
              y: 10
            }, false, 'plotBox')
          }
        }
      }
    },
    credits: {
      enabled: false
    },
    exporting: {
      enabled: true,
      buttons: {
        contextButton: {
          menuItems: ['viewFullscreen']
        }
      }
    },
    title: {
      text: '',
      align: 'center',
      verticalAlign: 'middle',
      style: {
        fontSize: '16px',
        fontFamily: 'Honeywell Sans Web'
      }
    },
    xAxis: {
      crosshair: true,
      title: {
        text: ''
      },
      type: 'datetime',
      labels: {
        formatter: xAxisLabelFormatter,
        useHTML: true,
        step: 2,
        allowOverlap: false
      },
      minRange: 60 * 1000
    },
    yAxis: yAxes,
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      itemMarginTop: 10,
      itemMarginBottom: 10,
      itemStyle: {
        fontWeight: '400',
        fontSize: '16px',
        fontFamily: '"Honeywell Sans Web", Arial, Helvetica, sans-serif'
      },
      backgroundColor: 'transparent',
      useHTML: true,
      labelFormatter: function () {
        const name = this.name
        const color = this.color
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `<div style="position: relative;"><span class='icon-class' style="background-color: ${this.visible ? color : color}; display: ${(this.name.length > 0) ? 'inline-table' : 'none'}"></span><span class='icon-class-title' style="color: '#f0f0f0'">${name}</span></div>`
      }
    },
    tooltip: {
      shared: true,
      split: true,
      valueDecimals: 0,
      xDateFormat: '%b %e, %Y %I:%M:%S %p',
      pointFormatter: customTooltipPointFormatter,
      positioner: tooltipPositioner
    },
    plotOptions: {
      series: {
        animation: series.length > 1000 ? false : { duration: 2000 }
      }
    },
    boost: {
      useGPUTranslations: true,
      usePreallocated: true
    },
    series,
    lang: {
      noData: 'No Data Available'
    },
    noData: {
      style: {
        fontWeight: 'bold',
        fontSize: '15px',
        color: '#f2f2f2'
      }
    },
    loading: {
      labelStyle: {
        color: 'white',
        fontWeight: 'bold',
        fontSize: '15px'
      },
      style: {
        backgroundColor: 'transparent'
      }
    }
  }
}
